/* =====================
   Shopify theme asset loader
   ===================== */
(() => {
  const manifest = window.themeAssetManifest || {};
  const scriptPromises = {};
  const stylePromises = {};
  const normalizeAssetUrl = (url) => {
    if (!url) return "";

    try {
      return new URL(url, window.location.origin).href;
    } catch (_error) {
      return url;
    }
  };

  const requestIdle =
    window.requestIdleCallback ||
    ((callback) => {
      window.setTimeout(callback, 1);
    });

  const loadScript = (src) => {
    if (!src) return Promise.resolve();
    if (scriptPromises[src]) return scriptPromises[src];

    const normalizedSrc = normalizeAssetUrl(src);
    const existing = Array.from(document.querySelectorAll("script[src]")).find(
      (script) =>
        script.dataset.themeAsset === src ||
        normalizeAssetUrl(script.getAttribute("src")) === normalizedSrc
    );

    if (existing) {
      scriptPromises[src] = Promise.resolve(existing);
      return scriptPromises[src];
    }

    scriptPromises[src] = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.defer = true;
      script.dataset.themeAsset = src;
      script.onload = () => resolve(script);
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return scriptPromises[src];
  };

  const loadStyle = (href) => {
    if (!href) return Promise.resolve();
    if (stylePromises[href]) return stylePromises[href];

    const normalizedHref = normalizeAssetUrl(href);
    const existing = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], link[as="style"]')
    ).find(
      (link) =>
        link.dataset.themeAsset === href ||
        normalizeAssetUrl(link.getAttribute("href")) === normalizedHref
    );

    if (existing) {
      stylePromises[href] = Promise.resolve(existing);
      return stylePromises[href];
    }

    stylePromises[href] = new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.themeAsset = href;
      link.onload = () => resolve(link);
      link.onerror = reject;
      document.head.appendChild(link);
    });

    return stylePromises[href];
  };

  const hasElement = (selector) => {
    try {
      return Boolean(document.querySelector(selector));
    } catch (_error) {
      return false;
    }
  };

  const loadWhenVisible = (
    selector,
    loader,
    options = { threshold: 0, rootMargin: "0px 0px 300px 0px" }
  ) => {
    const elements = document.querySelectorAll(selector);

    if (!elements.length) return;

    if (!("IntersectionObserver" in window)) {
      loader();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        loader();
      }
    }, options);

    elements.forEach((element) => observer.observe(element));
  };

  const preloadOnInteraction = (selector, loader) => {
    let loaded = false;

    const triggerLoad = () => {
      if (loaded) return;

      loaded = true;
      loader();

      document.removeEventListener("pointerover", handleEvent, true);
      document.removeEventListener("focusin", handleEvent, true);
      document.removeEventListener("touchstart", handleEvent, true);
      document.removeEventListener("click", handleEvent, true);
    };

    const handleEvent = (event) => {
      if (event.target?.closest(selector)) {
        triggerLoad();
      }
    };

    document.addEventListener("pointerover", handleEvent, true);
    document.addEventListener("focusin", handleEvent, true);
    document.addEventListener("touchstart", handleEvent, true);
    document.addEventListener("click", handleEvent, true);
  };

  const ensureSplideAssets = async () => {
    await Promise.all([
      loadStyle(manifest.styles?.splide),
      loadScript(manifest.scripts?.splide),
    ]);
  };

  const ensureCartAssets = async () => {
    const tasks = [];

    if (!manifest.flags?.cartStyleLoaded && manifest.styles?.cart) {
      tasks.push(loadStyle(manifest.styles.cart));
    }

    if (!manifest.flags?.cartLoaded && manifest.scripts?.cart) {
      tasks.push(loadScript(manifest.scripts.cart));
    }

    await Promise.all(tasks);
  };

  const ensureCollectionStyles = async () => {
    if (!manifest.flags?.collectionStyleLoaded && manifest.styles?.collection) {
      await loadStyle(manifest.styles.collection);
    }
  };

  const ensureProductAssets = async () => {
    const tasks = [ensureCartAssets()];

    if (!manifest.flags?.productStyleLoaded && manifest.styles?.product) {
      tasks.push(loadStyle(manifest.styles.product));
    }

    if (
      hasElement(
        "#product-template .splide, .product-gallery .splide, .sticky-atc .splide"
      ) &&
      (!manifest.flags?.splideLoaded || !manifest.flags?.splideStyleLoaded)
    ) {
      tasks.push(ensureSplideAssets());
    }

    if (
      document.body.classList.contains("page-type-product") &&
      !manifest.flags?.glightboxLoaded &&
      manifest.scripts?.glightbox
    ) {
      tasks.push(loadStyle(manifest.styles?.glightbox));
      tasks.push(loadScript(manifest.scripts.glightbox));
    }

    if (!manifest.flags?.productLoaded && manifest.scripts?.product) {
      tasks.push(loadScript(manifest.scripts.product));
    }

    await Promise.all(tasks);
  };

  const ensureSectionsAssets = async () => {
    const tasks = [];

    if (!manifest.flags?.sectionsStyleLoaded && manifest.styles?.sections) {
      tasks.push(loadStyle(manifest.styles.sections));
    }

    if (
      hasElement(
        ".featured-products .product-item, .recommended-products .product-item, .recently-viewed .product-item"
      ) &&
      !manifest.flags?.collectionStyleLoaded
    ) {
      tasks.push(ensureCollectionStyles());
    }

    if (
      hasElement(
        ".featured-products .splide, .recently-viewed .splide, .testimonials .splide, .instagram-gallery .splide, .tiktok-slider .splide"
      ) &&
      (!manifest.flags?.splideLoaded || !manifest.flags?.splideStyleLoaded)
    ) {
      tasks.push(ensureSplideAssets());
    }

    if (!manifest.flags?.sectionsLoaded && manifest.scripts?.sections) {
      tasks.push(loadScript(manifest.scripts.sections));
    }

    await Promise.all(tasks);
  };

  window.ensureShopiwebCartAssets = ensureCartAssets;
  window.ensureShopiwebCollectionStyles = ensureCollectionStyles;
  window.ensureShopiwebProductAssets = ensureProductAssets;
  window.ensureShopiwebSectionAssets = ensureSectionsAssets;

  const createDeferredProxy = (name, ensureAssets) => {
    if (typeof window[name] === "function") return;

    const proxy = async function proxyHandler(...args) {
      const possibleEvent = args.find(
        (arg) => arg && typeof arg.preventDefault === "function"
      );

      possibleEvent?.preventDefault();
      await ensureAssets();

      if (typeof window[name] === "function" && window[name] !== proxy) {
        return window[name](...args);
      }

      return undefined;
    };

    window[name] = proxy;
  };

  createDeferredProxy("handleAddToCartFormSubmit", ensureProductAssets);
  createDeferredProxy("handleAtcFormVariantClick", ensureProductAssets);
  createDeferredProxy("handleProductOptionChange", ensureProductAssets);
  createDeferredProxy("handleProductItemVariantChange", ensureProductAssets);
  createDeferredProxy("updateCartContents", ensureCartAssets);
  createDeferredProxy("handleCartItemRemoval", ensureCartAssets);
  createDeferredProxy("handleCartQuantityChange", ensureCartAssets);
  createDeferredProxy("handleCartNoteSave", ensureCartAssets);
  createDeferredProxy("fetchShippingRates", ensureCartAssets);

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      const deferredSectionsSelector =
        ".recommended-products, .featured-products, .recently-viewed, .testimonials, .instagram-gallery, .tiktok-slider, .marquee, .scrolling, .sticky-video, .discount-bar, .animated-counters, .progress-circle, .parallax-image, .image-compare, .animated-stories, section.quiz, #sticky-atc";

      if (
        !manifest.flags?.sectionsLoaded &&
        hasElement(deferredSectionsSelector)
      ) {
        loadWhenVisible(
          deferredSectionsSelector,
          () => requestIdle(() => ensureSectionsAssets()),
          { threshold: 0, rootMargin: "0px 0px 350px 0px" }
        );
      }

      if (!manifest.flags?.productLoaded) {
        loadWhenVisible(
          ".featured-products, .recently-viewed, .shopify-product-form, .product-carousel, .sticky-atc",
          () => requestIdle(() => ensureProductAssets()),
          { threshold: 0, rootMargin: "0px 0px 300px 0px" }
        );
      }

      if (!manifest.flags?.cartLoaded && document.body.classList.contains("page-type-cart")) {
        requestIdle(() => {
          ensureCartAssets();
        });
      }

      if (!manifest.flags?.cartLoaded) {
        preloadOnInteraction(
          '[href="#offcanvas-cart"], [data-bs-target="#offcanvas-cart"], [aria-controls="offcanvas-cart"]',
          () => requestIdle(() => ensureCartAssets())
        );
      }

      if (!manifest.flags?.productLoaded) {
        preloadOnInteraction(
          ".shopify-product-form, .product-item form, .btn-atc, .product-options, .product-carousel",
          () => requestIdle(() => ensureProductAssets())
        );
      }
    },
    { once: true }
  );
})();
